import { Observable, firstValueFrom } from 'rxjs';
import { TransferState, makeStateKey } from '@angular/core';
import { PLATFORM_ID, Injectable, Inject, Injector } from '@angular/core';
import { isPlatformServer, isPlatformBrowser } from '@angular/common';
import { ShellDomainProvider } from '../utility/shell-domain.provider';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { EcapCookieService } from '../cookie/ecap-cookie.service';

@Injectable()
export class TokenProvider {
    private isPlatformServer: boolean;
    private accessToken: string;
    private refreshToken: string;
    private gettingAccessToken: boolean;
    private isPlatformBrowser: boolean;
    constructor(
        private state: TransferState,
        @Inject(PLATFORM_ID) platformId: string,
        private _cookieService: EcapCookieService,
        private injector: Injector) {
        // console.log('=====TokenProvider INITIALIZED=====');
        this.isPlatformServer = isPlatformServer(platformId);
        this.isPlatformBrowser = isPlatformBrowser(platformId);
        if (this.isPlatformServer) {
            // console.log('setting access token and refresh token in provider from process.env');
            this.accessToken = process.env.ACCESS_TOKEN;
            this.refreshToken = process.env.REFRESH_TOKEN;
        } else {
            let currentOrigin = window.location.hostname;
            this.accessToken = this._cookieService.getCookie(currentOrigin);
            this.refreshToken = this._cookieService.getCookie("REFRESH_TOKEN");
        }
    }

    public getAccessToken() {
        return this.accessToken;
    }

    public getRefreshToken() {
        if (this.isPlatformServer) {
            return process.env.REFRESH_TOKEN;
        } else {
            return this._cookieService.getCookie("REFRESH_TOKEN");
            //const expectedCookie = this._cookieService.getCookie("REFRESH_TOKEN");
            //return this.refreshToken || expectedCookie;
        }
    }

    public setAccessToken(token) {
        this.accessToken = token;
    }

    public setAccessTokenToCookie(token) {
        //document.cookie = `ACCESS_TOKEN=${token}`;
    }

    public setRefreshToken(token) {
        this.refreshToken = token;
    }

    public setRefreshTokenToCookie(refreshToken) {
        if (!this.isPlatformBrowser) {
            return;
        }
    
        const minutesToLive = ShellDomainProvider['api'].refreshTokenMinutesToLive || 30*24*60; // 1 month in minutes
    
        const httpOnlyRefreshToken = ShellDomainProvider['api'].httpOnlyRefreshToken;
    
        const newRefreshToken = httpOnlyRefreshToken ? 'REFRESH_TOKEN' : refreshToken;
    
        this._cookieService.eraseAndSetCookie('REFRESH_TOKEN', newRefreshToken, minutesToLive);
    }

    public setRefreshTokenForNodeResponse(tokenString) {
        let targetDataStructure;
        if (!process.env.cookiesForResponse) {
            targetDataStructure = [];
        } else {
            targetDataStructure = JSON.parse(process.env.cookiesForResponse);
        }
        targetDataStructure.push(tokenString);
        process.env.cookieArray = JSON.stringify(targetDataStructure);
    }

    public refreshTokenObservable(): Observable<any> {
        const http = this.injector.get(HttpClient);
        // Create a new HttpParams object and set the grant_type and client_id parameters
		let body = new HttpParams()
        .set('grant_type', 'refresh_token')
        .set('client_id', ShellDomainProvider['api'].Client_ID);

        // If httpOnlyRefreshToken is not set, append 'refresh_token' to the body
        if (!ShellDomainProvider['api']?.httpOnlyRefreshToken) {
            body = body.append('refresh_token', this.getRefreshToken());
        }
        const header = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
        });
        return http.post(ShellDomainProvider['api'].Token, body.toString(), { headers: header, withCredentials: true }).pipe(tap((response: any) => {
            this.set_may_access(response);
            this.gettingAccessToken = false;
        }));
    }

    public set_may_access(response: any) {
        const responseObj = {
            may_access: null
        };

        if (response && response.may_access) {
            responseObj.may_access = this.getFilteredSSODomains(response.may_access);
            if (this.isPlatformBrowser)
                localStorage.setItem("may_access", responseObj.may_access);
            else
                process.env.may_access = responseObj.may_access;
        }

        return responseObj;
    }

    private getFilteredSSODomains(may_access: any) {
        const ssoIgnoreDomains = ShellDomainProvider['api']['ssoIgnoreDomains'];

        if (ssoIgnoreDomains && ssoIgnoreDomains.length) {
            let accessDomainList = may_access.split(',');

            accessDomainList = accessDomainList.filter((el: string) => !ssoIgnoreDomains.includes(el));

            return accessDomainList.join(',');
        }

        return may_access;
    }

    public getAnnonymousToken(): Promise<any> {
        const http = this.injector.get(HttpClient);
        const body = new HttpParams().set('grant_type', 'authenticate_site');
        const header = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
        });

        return firstValueFrom(http.post(ShellDomainProvider['api'].Token,
            body.toString(),
            { headers: header, withCredentials: true }));
    }

    public securityLogout(): Promise<any> {
        let refreshToken = this.refreshToken;
        const http = this.injector.get(HttpClient);
        const header = new HttpHeaders({
            'Content-Type': 'application/json'
        });
        // itsm config
        let userInfo = {
            name: "",
            email: "",
            isLoggedIn: false
        }
        window['supportITSM'] && window['supportITSM'].emitEvent({ config: userInfo});
        const body = { 'RefreshToken': refreshToken }
        return firstValueFrom(http.post(ShellDomainProvider['api'].SecurityIdentity + "Authentication/logout", body, { headers: header, withCredentials: true }));
    }
}
